import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useFormatters } from "../components/shared/useFormatters";
import { useAuth } from "../context/AuthContext";
import { BUILD_VERSION, BUILD_INFO, APP_VERSION, BUILD_DATE, BUILD_TIMESTAMP } from "../lib/version";
import {
  Database,
  Server,
  Clock,
  HardDrive,
  FileText,
  Users,
  MapPin,
  FolderKanban,
  Calendar,
  ClipboardList,
  UserCheck,
  Scale,
  Receipt,
  RefreshCw,
  Building2,
  Mail,
  Upload,
  Info,
  Tag,
  Trash2,
  AlertTriangle,
  Loader2,
  Download,
  FileCode,
  BookOpen,
  Package,
  Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { LoadingSpinner } from "../components/shared/DataComponents";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../lib/api";

// Collection icons mapping
const COLLECTION_ICONS = {
  clients: Users,
  locations: MapPin,
  projects: FolderKanban,
  shifts: Calendar,
  staff: UserCheck,
  task_definitions: ClipboardList,
  shift_tasks: ClipboardList,
  assignments: Calendar,
  staff_availability: Clock,
  quotes: FileText,
  invoices: Receipt,
  rules: Scale,
  company_profile: Building2,
  email_providers: Mail,
  import_history: Upload,
};

// Collection labels
const COLLECTION_LABELS = {
  clients: "Clients",
  locations: "Locations",
  projects: "Projects",
  shifts: "Shifts",
  staff: "Crew Members",
  task_definitions: "Task Definitions",
  shift_tasks: "Shift Tasks",
  assignments: "Assignments",
  staff_availability: "Crew Availability",
  quotes: "Quotes",
  invoices: "Invoices",
  rules: "Rules",
  company_profile: "Company Profile",
  email_providers: "Email Providers",
  import_history: "Import History",
};

export default function SystemInfoPage() {
  const { formatDate, formatTime } = useFormatters();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);
  
  // EF-SYSTEM-SNAPSHOT-DOWNLOAD-V1: Check if user is superadmin
  const isSuperAdmin = user?.role === "superadmin";
  
  // EF-SNAPSHOT-AUTH-FIX-V2: Download state management
  const [downloading, setDownloading] = useState(null);
  
  // EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Test data cleanup state
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // EF-SNAPSHOT-AUTH-FIX-V2: Authenticated download helper
  const downloadFile = async (endpoint, filename, downloadKey) => {
    try {
      setDownloading(downloadKey);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Downloaded ${filename}`);

    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Download failed: ${error.message}`);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    loadSystemInfo();
  }, []);

  const loadSystemInfo = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await api.get("/system/info");
      setSystemInfo(response.data);

      if (showRefresh) toast.success("System info refreshed");
    } catch (err) {
      toast.error("Failed to load system info");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Handle test data cleanup
  const handleCleanupTestData = async () => {
    try {
      setCleanupLoading(true);
      const response = await api.delete("/admin/test-data/projects");
      const { projects_deleted, shifts_deleted, projects_found, message } = response.data;
      
      setCleanupDialogOpen(false);
      
      if (projects_deleted > 0 || shifts_deleted > 0) {
        toast.success(`Deleted ${projects_deleted} projects and ${shifts_deleted} shifts`, {
          description: projects_found?.length > 0 
            ? `Projects: ${projects_found.slice(0, 5).join(', ')}${projects_found.length > 5 ? '...' : ''}`
            : undefined,
          duration: 5000,
        });
        // Refresh system info to show updated counts
        loadSystemInfo(true);
      } else {
        toast.info("No test data found to delete", {
          description: message,
        });
      }
    } catch (err) {
      console.error("Cleanup error:", err);
      const errorMsg = err.response?.data?.detail || "Failed to clean up test data";
      toast.error(errorMsg);
    } finally {
      setCleanupLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "Never";
    try {
      return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
    } catch {
      return "Invalid date";
    }
  };

  const getTotalRecords = () => {
    if (!systemInfo?.collections) return 0;
    return Object.values(systemInfo.collections).reduce((a, b) => a + b, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-info-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold tracking-tight">System Info</h1>
          <p className="text-sm text-muted-foreground">
            Database statistics and system information (read-only)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => loadSystemInfo(true)}
          disabled={refreshing}
          data-testid="refresh-system-info-btn"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Version Information Card */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <Info className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Version Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg" data-testid="platform-version-info">
            <div className="p-2 rounded bg-primary/10">
              <Tag className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">Platform Version</p>
              <p className="text-lg font-semibold font-mono">{BUILD_VERSION}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg" data-testid="pwa-version-info">
            <div className="p-2 rounded bg-blue-500/10">
              <Server className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">PWA Version</p>
              <p className="text-lg font-semibold font-mono">{BUILD_VERSION}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg" data-testid="build-timestamp-info">
            <div className="p-2 rounded bg-green-500/10">
              <Clock className="h-4 w-4 text-green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">Build Timestamp</p>
              <p className="text-lg font-semibold font-mono">{BUILD_TIMESTAMP}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg" data-testid="app-version-info">
            <div className="p-2 rounded bg-purple-500/10">
              <Tag className="h-4 w-4 text-purple-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground">App Version</p>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold font-mono">{APP_VERSION}</p>
                <Badge variant="outline" className="text-xs">
                  {process.env.NODE_ENV === "production" ? "Production" : "Preview"}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card/50 border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Server className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Environment</p>
              <p className="text-xl font-semibold capitalize">
                {systemInfo?.environment || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Database className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Database</p>
              <p className="text-xl font-semibold font-mono">
                {systemInfo?.database_name || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <HardDrive className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Records</p>
              <p className="text-xl font-semibold">{getTotalRecords().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Collection Counts */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <Database className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Collection Statistics</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Object.entries(systemInfo?.collections || {}).map(([name, count]) => {
            const Icon = COLLECTION_ICONS[name] || Database;
            const label = COLLECTION_LABELS[name] || name.replace(/_/g, " ");

            return (
              <div
                key={name}
                className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                data-testid={`collection-count-${name}`}
              >
                <div className="p-2 rounded bg-white/5">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground truncate capitalize">
                    {label}
                  </p>
                  <p className="text-lg font-semibold font-mono">{count.toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Import History */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <Upload className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Last Import Timestamps</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Entity Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Last Import
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {Object.entries(systemInfo?.last_imports || {}).map(
                ([entityType, lastImport]) => (
                  <tr key={entityType} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-sm capitalize">
                      {entityType.replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lastImport ? (
                        <span className="text-green-500">
                          {formatDateTime(lastImport)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Never imported</span>
                      )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Admin Tools Section */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <Trash2 className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-semibold">Admin Tools</h2>
          <Badge variant="outline" className="ml-2 text-xs text-amber-500 border-amber-500/50">
            Superadmin Only
          </Badge>
        </div>

        <div className="mt-4 space-y-4">
          <div className="flex items-start justify-between gap-4 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <div className="flex-1">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Clean Test Data
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Delete test/demo projects and their associated shifts. Removes projects where:
              </p>
              <ul className="text-xs text-muted-foreground mt-2 ml-4 list-disc space-y-1">
                <li>Created by system/emergent</li>
                <li>Marked as test data</li>
                <li>Name contains "test", "demo", or "sample"</li>
              </ul>
              <p className="text-xs text-amber-500 mt-2 font-medium">
                ⚠️ This action cannot be undone. Real production data is preserved.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
              onClick={() => setCleanupDialogOpen(true)}
              data-testid="clean-test-data-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clean Test Data
            </Button>
          </div>
        </div>
      </div>

      {/* EF-DOCS-DOWNLOAD-ENDPOINTS-V1: Documentation Downloads Section */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <BookOpen className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold">Documentation Downloads</h2>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/current-state", "EVENTSFLO-CURRENT-STATE.md", "current-state")}
            disabled={downloading === "current-state"}
            data-testid="download-current-state-btn"
          >
            {downloading === "current-state" ? (
              <Loader2 className="h-4 w-4 text-green-500 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 text-green-500" />
            )}
            {downloading === "current-state" ? "Downloading..." : "Current State"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/architecture", "EVENTSFLO-SYSTEM-ARCHITECTURE.md", "architecture")}
            disabled={downloading === "architecture"}
            data-testid="download-architecture-btn"
          >
            {downloading === "architecture" ? (
              <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
            ) : (
              <FileCode className="h-4 w-4 text-purple-500" />
            )}
            {downloading === "architecture" ? "Downloading..." : "Architecture"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/ai-control", "EVENTSFLO-AI-DEVELOPMENT-CONTROL.md", "ai-control")}
            disabled={downloading === "ai-control"}
            data-testid="download-ai-control-btn"
          >
            {downloading === "ai-control" ? (
              <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 text-amber-500" />
            )}
            {downloading === "ai-control" ? "Downloading..." : "AI Control"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/readme", "README.md", "readme")}
            disabled={downloading === "readme"}
            data-testid="download-readme-btn"
          >
            {downloading === "readme" ? (
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
            ) : (
              <BookOpen className="h-4 w-4 text-blue-500" />
            )}
            {downloading === "readme" ? "Downloading..." : "README"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/quote-system", "EVENTSFLO-QUOTE-SYSTEM.md", "quote-system")}
            disabled={downloading === "quote-system"}
            data-testid="download-quote-system-btn"
          >
            {downloading === "quote-system" ? (
              <Loader2 className="h-4 w-4 text-cyan-500 animate-spin" />
            ) : (
              <Receipt className="h-4 w-4 text-cyan-500" />
            )}
            {downloading === "quote-system" ? "Downloading..." : "Quote System"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/pdf-structure", "EVENTSFLO-PDF-STRUCTURE.md", "pdf-structure")}
            disabled={downloading === "pdf-structure"}
            data-testid="download-pdf-structure-btn"
          >
            {downloading === "pdf-structure" ? (
              <Loader2 className="h-4 w-4 text-red-500 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 text-red-500" />
            )}
            {downloading === "pdf-structure" ? "Downloading..." : "PDF Structure"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/development-rules", "EVENTSFLO-DEVELOPMENT-RULES.md", "dev-rules")}
            disabled={downloading === "dev-rules"}
            data-testid="download-dev-rules-btn"
          >
            {downloading === "dev-rules" ? (
              <Loader2 className="h-4 w-4 text-orange-500 animate-spin" />
            ) : (
              <Scale className="h-4 w-4 text-orange-500" />
            )}
            {downloading === "dev-rules" ? "Downloading..." : "Dev Rules"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-2"
            onClick={() => downloadFile("/api/system/docs/priorities", "NEXT-CHAT-PRIORITIES.md", "priorities")}
            disabled={downloading === "priorities"}
            data-testid="download-priorities-btn"
          >
            {downloading === "priorities" ? (
              <Loader2 className="h-4 w-4 text-pink-500 animate-spin" />
            ) : (
              <ClipboardList className="h-4 w-4 text-pink-500" />
            )}
            {downloading === "priorities" ? "Downloading..." : "Priorities"}
            <Download className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Download system documentation files for reference or handoff.
        </p>
      </div>

      {/* EF-SYSTEM-SNAPSHOT-DOWNLOAD-V1: System Snapshot Section (SuperAdmin only) */}
      {isSuperAdmin && (
        <div className="bg-card/50 border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <Package className="h-5 w-5 text-emerald-500" />
            <h2 className="text-lg font-semibold">System Snapshot</h2>
            <Badge variant="outline" className="ml-2 text-xs text-emerald-500 border-emerald-500/50">
              <Shield className="h-3 w-3 mr-1" />
              Superadmin Only
            </Badge>
          </div>

          <div className="mt-4 flex items-start justify-between gap-4 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
            <div className="flex-1">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <Package className="h-4 w-4 text-emerald-500" />
                Download System Snapshot
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Export all core system documentation as a dated ZIP file.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Includes: README, Current State, Architecture, AI Control, Quote System, PDF Structure, Dev Rules, Priorities
              </p>
              <p className="text-xs text-emerald-500 mt-2 font-medium">
                Export date: {new Date().toISOString().split('T')[0]}
              </p>
            </div>
            <Button
              variant="outline"
              className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
              onClick={() => downloadFile(
                "/api/system/snapshot",
                `eventsflo-system-snapshot-${new Date().toISOString().split('T')[0]}.zip`,
                "snapshot"
              )}
              disabled={downloading === "snapshot"}
              data-testid="download-system-snapshot-btn"
            >
              {downloading === "snapshot" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download ZIP
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* System Info Footer */}
      <div className="bg-card/50 border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10">
          <Server className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">System Details</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
          <div>
            <span className="text-muted-foreground">Platform Version:</span>
            <span className="ml-2 font-mono">{BUILD_VERSION}</span>
          </div>
          <div>
            <span className="text-muted-foreground">App Version:</span>
            <span className="ml-2 font-mono">{APP_VERSION}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Build Date:</span>
            <span className="ml-2 font-mono">{BUILD_DATE}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Environment:</span>
            <span className="ml-2 font-mono capitalize">{systemInfo?.environment || "development"}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-xs text-muted-foreground">
            This page provides read-only visibility into the database structure and usage.
            No destructive actions are available here.
          </p>
        </div>
      </div>

      {/* EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Cleanup Confirmation Dialog */}
      <AlertDialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Delete Test Data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will permanently delete test projects and shifts.
              </p>
              <p className="font-medium text-foreground">
                This action cannot be undone.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Only projects matching test/demo criteria will be deleted. Real production data will not be affected.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cleanupLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanupTestData}
              disabled={cleanupLoading}
              className="bg-amber-500 text-black hover:bg-amber-600"
              data-testid="confirm-cleanup-btn"
            >
              {cleanupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Test Data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
